export const OFFERWALL_ACCESS_KEY = 'haber:offerwall:accessUntil'
export const OFFERWALL_ACCESS_EVENT = 'haber:offerwall-access-changed'

/** null means storage is unavailable; the soft gate must stay open in that case. */
export function readOfferwallAccessUntil(): number | null {
  if (typeof window === 'undefined') return 0
  try {
    const until = Number(window.localStorage.getItem(OFFERWALL_ACCESS_KEY))
    return Number.isFinite(until) && until > Date.now() ? until : 0
  } catch {
    return null
  }
}

export function hasOfferwallAccess(): boolean {
  const until = readOfferwallAccessUntil()
  return until === null || until > Date.now()
}

/** Preloaded articles are not read yet; an expired grant must not keep those open. */
export function rememberOfferwallArticleAccess(alreadyRead: boolean, isVisible: boolean): boolean {
  return alreadyRead || (isVisible && hasOfferwallAccess())
}

export function shouldResetOfferwallAttempt(
  previous: { enabled: boolean; path: string; minutes: number } | null,
  next: { enabled: boolean; path: string; minutes: number },
): boolean {
  return previous !== null && (previous.enabled !== next.enabled || previous.path !== next.path)
}

/** Called only after GPT confirms the reward, never for an unavailable ad. */
export function grantOfferwallAccess(minutes: number): number {
  const duration = Number.isFinite(minutes) ? Math.min(1440, Math.max(1, minutes)) : 30
  const until = Date.now() + duration * 60_000
  try {
    window.localStorage.setItem(OFFERWALL_ACCESS_KEY, String(until))
  } catch {
    // Storage restrictions already make the soft gate fail open.
  }
  window.dispatchEvent(new Event(OFFERWALL_ACCESS_EVENT))
  return until
}

/** localStorage does not emit storage events in the tab that wrote the value. */
export function subscribeOfferwallAccess(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === OFFERWALL_ACCESS_KEY || event.key === null) onChange()
  }
  window.addEventListener(OFFERWALL_ACCESS_EVENT, onChange)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(OFFERWALL_ACCESS_EVENT, onChange)
    window.removeEventListener('storage', onStorage)
  }
}
